import BaseOption from "%/dynamic-component-editor/classes/base-option";
import {
  CLICK_GESTURE_CLICK,
  CLICK_GESTURE_ORDER,
  PAGE_SELECTION_TYPE_NONE,
} from "%/dynamic-component-editor/constants/click-action-constants";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import LinkType from "%/dynamic-component-editor/options/link-type";
import {
  UI_LINK_TYPE_URL,
  storedLinkTypeFor,
} from "%/dynamic-component-editor/helpers/click-action-page-options";
import { SINGLE_FINGER } from "@bloglovin/vue-component-library/gesture";
import { BEHAVIOUR_NAVIGATE, behaviourOf } from "@bloglovin/vue-component-library/behaviour";

export default class ClickAction extends BaseOption {
  static userInterfaceComponent = {
    label: "Click Action",
    component: inspectorTools.CLICK_ACTION,
    useCustomLabel: true,
    useVerticalOrientation: true,
    useCustomLabelWhenVariableLinked: true,
  };

  static defaultValue = {
    action: "goToUrl",
    modal: "",
    label: "",
    linkType: LinkType.LINK_TYPE_HYPERLINK,
    page: "",
    pageSelectionType: PAGE_SELECTION_TYPE_NONE,
    value: "",
    contextSource: null,
    uriVarSources: {},
    openInNewTab: false,
    hasUpdated: false,
  };

  static getModifiedRawValue(option, optionValues, state) {
    if (option.hasUpdated) {
      return {
        ...this.defaultValue,
        ...option,
      };
    }

    let openInNewTab = optionValues[`${state}.open-in-new-tab`] === "true";

    return {
      action: optionValues[`${state}.button-action`] ?? "goToUrl",
      label: optionValues[`${state}.label`] ?? "",
      modal: optionValues[`${state}.modal`] ?? "",
      linkType: optionValues[`${state}.link-type`] ?? LinkType.LINK_TYPE_HYPERLINK,
      value: optionValues[`${state}.href-option`] ?? "",
      openInNewTab: openInNewTab,
      hasUpdated: false,
      page: "",
      pageSelectionType: PAGE_SELECTION_TYPE_NONE,
      uriVarSources: {},
    };
  }

  static convertConfigValueToCompiledValue(configValue, themeId, themeMode, optionValues = {}) {
    if (configValue.hasUpdated) {
      return {
        ...this.defaultValue,
        ...configValue,
      };
    }

    return {
      action: optionValues["default.button-action"] ?? "goToUrl",
      label: optionValues["default.label"] ?? "",
      modal: optionValues["default.modal"] ?? "",
      linkType: optionValues["default.link-type"] ?? LinkType.LINK_TYPE_HYPERLINK,
      value: optionValues["default.href-option"] ?? "",
      openInNewTab: optionValues["default.open-in-new-tab"] ?? false,
      hasUpdated: false,
      page: "",
      pageSelectionType: PAGE_SELECTION_TYPE_NONE,
      uriVarSources: {},
    };
  }

  static convertValueToStringForTransactionLog() {
    return "Updated click action";
  }

  static baseActionFrom(value) {
    if (!value || typeof value !== "object") {
      return {};
    }

    const baseAction = { ...value };
    delete baseAction.gestures;

    return baseAction;
  }

  static gesturesFrom(value) {
    if (!value || typeof value !== "object" || !value.gestures || typeof value.gestures !== "object") {
      return {};
    }

    return value.gestures;
  }

  // A tap counts as set up when it has somewhere to go OR something to do.
  // Only navigation needs a destination; playing, scaling and the rest are
  // complete on their own, and reading them as unset dropped the row.
  static isActionConfigured(action) {
    if (!action || typeof action !== "object") {
      return false;
    }
    if (behaviourOf(action).kind !== BEHAVIOUR_NAVIGATE) {
      return true;
    }

    if (action.linkType === LinkType.LINK_TYPE_PAGE && action.hasUpdated === true) {
      return true;
    }

    return Boolean(action.value || action.page || action.modal);
  }

  static assignedGestures(value) {
    const gestures = Object.keys(this.gesturesFrom(value))
        .filter(gesture => gesture !== CLICK_GESTURE_CLICK && CLICK_GESTURE_ORDER.includes(gesture));
    const assigned = this.isActionConfigured(this.baseActionFrom(value))
      ? [CLICK_GESTURE_CLICK, ...gestures]
      : gestures;

    return assigned.sort(
      (first, second) => CLICK_GESTURE_ORDER.indexOf(first) - CLICK_GESTURE_ORDER.indexOf(second),
    );
  }

  static assignedGestureEntries(value) {
    return this.assignedGestures(value).map(gesture => {
      const action = this.actionForGesture(value, gesture);

      return {
        id: gesture,
        fingers: action.fingers ?? SINGLE_FINGER,
        hold: action.hold === true,
      };
    });
  }

  static actionForGesture(value, gesture) {
    if (gesture === CLICK_GESTURE_CLICK) {
      return this.baseActionFrom(value);
    }

    return this.gesturesFrom(value)[gesture] ?? { ...this.defaultValue };
  }

  static withGestureAction(value, gesture, action) {
    const gestures = this.gesturesFrom(value);

    if (gesture === CLICK_GESTURE_CLICK) {
      const baseAction = this.baseActionFrom(action);

      return Object.keys(gestures).length ? { ...baseAction, gestures } : baseAction;
    }

    return {
      ...this.baseActionFrom(value),
      gestures: { ...gestures, [gesture]: this.baseActionFrom(action) },
    };
  }

  static withoutGesture(value, gesture) {
    const gestures = { ...this.gesturesFrom(value) };

    if (gesture === CLICK_GESTURE_CLICK) {
      return Object.keys(gestures).length
        ? { ...this.defaultValue, gestures }
        : { ...this.defaultValue };
    }

    delete gestures[gesture];

    return Object.keys(gestures).length
      ? { ...this.baseActionFrom(value), gestures }
      : this.baseActionFrom(value);
  }

  static withUiLinkType(action, uiLinkType) {
    const current = { ...this.defaultValue, ...this.baseActionFrom(action) };

    if (uiLinkType !== UI_LINK_TYPE_URL) {
      return { ...current, linkType: storedLinkTypeFor(uiLinkType) };
    }

    return {
      ...current,
      linkType: storedLinkTypeFor(uiLinkType),
      page: "",
      pageSelectionType: PAGE_SELECTION_TYPE_NONE,
      contextSource: null,
      uriVarSources: {},
      value: "",
    };
  }

  static withGestureMovedTo(value, fromGesture, toGesture) {
    if (fromGesture === toGesture) {
      return value;
    }

    const action = this.actionForGesture(value, fromGesture);

    return this.withGestureAction(this.withoutGesture(value, fromGesture), toGesture, action);
  }
}
